import React, { type ComponentProps, useCallback, useRef, useState } from 'react';
import type { ViewabilityConfig } from '@react-native/virtualized-lists';
import {
  FlatList,
  type LayoutChangeEvent,
  type ListRenderItem,
  StyleSheet,
  View,
} from 'react-native';

import { ProductCardConstants, theme, useUniqueNativeID } from '@sg/garnish';

import { ProductDetailsCardCarouselDots, ProductDetailsCardCarouselSlide } from './components';
import { type ProductDetailsCardCarouselItem } from './ProductDetailsCardCarousel.types';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsCardCarousel = (props: Props) => {
  const { items, initialSlideIndex = 0, onActiveSlideChange } = props;

  const uniqueId = useUniqueNativeID('product-card-carousel');

  // ─── Refs ────────────────────────────────────────────────────────────

  const flatListRef = useRef<FlatList>(null);

  // ─── State ───────────────────────────────────────────────────────────

  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(initialSlideIndex);

  // ─────────────────────────────────────────────────────────────────────

  const slideWidth = containerWidth ? containerWidth * 0.9 : 0;
  const slideGap = theme.spacing['2'];
  const snapToInterval = slideWidth ? slideWidth + slideGap : undefined;

  // ─── Helpers ─────────────────────────────────────────────────────────

  // istanbul ignore next
  const handleViewableItemsChanged = useCallback<OnViewableItemsChanged>(
    (params) => {
      const { viewableItems } = params;

      const hasViewableItems = viewableItems.length > 0;
      const activeSlideIndex = viewableItems?.[0]?.index;
      const hasActiveSlide = hasViewableItems && typeof activeSlideIndex === 'number'; // prettier-ignore

      if (!hasActiveSlide) return;

      setActiveIndex(activeSlideIndex);
      onActiveSlideChange?.(activeSlideIndex);
    },
    [onActiveSlideChange],
  );

  const onScrollToIndexFailed = useCallback<OnScrollToIndexFailed>((params) => {
    const { index } = params;

    requestIdleCallback(() => {
      flatListRef.current?.scrollToIndex({ index, animated: true });
    });
  }, []);

  const handleContainerLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width } = event.nativeEvent.layout;
      const shouldUpdateWidth = width > 0 && width !== containerWidth;

      if (!shouldUpdateWidth) return;

      setContainerWidth(width);
    },
    [containerWidth],
  );

  const handleGetItemLayout = useCallback<GetItemLayout>(
    (_data, index) => {
      const slideWidthWithFallback = slideWidth ?? 0;

      return {
        length: slideWidthWithFallback + slideGap,
        offset: (slideWidthWithFallback + slideGap) * index,
        index,
      };
    },
    [slideGap, slideWidth],
  );

  const keyExtractor = useCallback<KeyExtractor>(
    (_, index) => `${uniqueId}-item-${index}`,
    [uniqueId],
  );

  // ─────────────────────────────────────────────────────────────────────

  const renderItem: ListRenderItem<ProductDetailsCardCarouselItem> = useCallback(
    (params) => {
      const { item, index } = params;

      return (
        <ProductDetailsCardCarouselSlide
          item={item}
          slideWidth={slideWidth}
          slideGap={slideGap}
          isLast={index === items.length - 1}
          isActive={index === activeIndex}
        />
      );
    },
    [slideWidth, slideGap, items.length, activeIndex],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasMultipleItems = items.length > 1;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container} onLayout={handleContainerLayout}>
      {containerWidth === null ? null : (
        <>
          <FlatList
            ref={flatListRef}
            data={items}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            decelerationRate="fast"
            snapToAlignment="start"
            snapToInterval={snapToInterval}
            onViewableItemsChanged={handleViewableItemsChanged}
            viewabilityConfig={viewabilityConfig}
            onScrollToIndexFailed={onScrollToIndexFailed}
            getItemLayout={handleGetItemLayout}
            scrollEventThrottle={16}
            windowSize={3}
            removeClippedSubviews={false}
            maxToRenderPerBatch={2}
            updateCellsBatchingPeriod={50}
            initialNumToRender={2}
            initialScrollIndex={initialSlideIndex}
          />

          {hasMultipleItems ? (
            <ProductDetailsCardCarouselDots items={items} activeIndex={activeIndex} />
          ) : null}
        </>
      )}
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const viewabilityConfig: ViewabilityConfig = {
  itemVisiblePercentThreshold: 50,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    height: ProductCardConstants.IMAGE_SIZE,
    borderTopRightRadius: theme.radius.large,
    borderTopLeftRadius: theme.radius.large,
    overflow: 'hidden',
    marginHorizontal: -ProductCardConstants.CONTAINER_PADDING_X_MEDIUM,
    marginBottom: theme.spacing['1'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  items: ProductDetailsCardCarouselItem[];
  initialSlideIndex?: number;
  onActiveSlideChange?: (activeSlideIndex: number) => void;
};

type OnViewableItemsChanged = NonNullable<FlatListProps['onViewableItemsChanged']>; // prettier-ignore
type OnScrollToIndexFailed = NonNullable<FlatListProps['onScrollToIndexFailed']>; // prettier-ignore
type GetItemLayout = NonNullable<FlatListProps['getItemLayout']>;
type KeyExtractor = NonNullable<FlatListProps['keyExtractor']>;
type FlatListProps = ComponentProps<typeof FlatList>;
